import {useEffect, useRef, useState} from 'react'
import {TRACKING_LOADING_DELAY} from '../../constants'
import useOcapiSite from '../../commerce-api/hooks/useOcapiSite'

/**
 * a hook to load interaction studio pixel
 * @param {boolean} loadImmediately - if true, load the pixel immediately, otherwise wait for the TRACKING_LOADING_DELAY
 */
const useInteractionStudioPixel = ({loadImmediately} = {}) => {
    const {isInteractionStudioEnabled, interactionStudioJS} = useOcapiSite().preferences
    // use local state to trigger the loading of the script
    const [load, setLoad] = useState(loadImmediately)
    // use ref as a workaround to avoid including interactionStudioJS in the dependency array
    // there's no way to unload the script once it's loaded, so any change in the interactionStudioJS
    // will just result in duplicate scripts
    const srcRef = useRef(interactionStudioJS)

    // activate loading immediately or after a delay
    useEffect(() => {
        if (!isInteractionStudioEnabled || !interactionStudioJS) {
            return
        }

        if (loadImmediately) {
            setLoad(true)
            return
        }

        const timer = setTimeout(() => {
            setLoad(true)
        }, TRACKING_LOADING_DELAY)
        return () => clearTimeout(timer)
    }, [interactionStudioJS, isInteractionStudioEnabled, loadImmediately])

    // load the script - should execute only once
    useEffect(() => {
        if (load) {
            // directly modify the dom as opposed to using helmet:
            // 1. helmet requires to return component
            // 2. this way the script is not rendered by ssr
            const script = document.createElement('script')
            script.src = srcRef.current
            script.async = true
            document.body.appendChild(script)
            return () => {
                document.body.removeChild(script)
            }
        }
    }, [load])
}

export default useInteractionStudioPixel
